/**
 * Display helpers for DICOM wire-format strings.
 *
 * Both functions accept the raw DICOM value (or null/undefined) and
 * return the user-facing string, or `null` when the input is empty.
 * Unparseable input is returned unchanged rather than dropped so a
 * clinician never silently loses data — they can still read the raw
 * value in the "Technical details" disclosure if they need to.
 */

/**
 * Convert a DICOM PN (Person Name) string to a display name.
 *
 * DICOM PN format: `FAMILY^GIVEN^MIDDLE^PREFIX^SUFFIX` (caret-separated
 * components, all optional). Multi-cultural names can use an `=` to
 * separate the alphabetic, ideographic, and phonetic representations
 * — we only render the first (alphabetic) group, matching what every
 * vendor's viewer does.
 *
 * Output is `Last, First` when both components are present, falls back
 * to whichever components exist, and finally to the raw input if
 * nothing parsed.
 *
 * Examples:
 *   `JOHNSON^Michael`     → `Johnson, Michael`
 *   `Doe^John^A^Dr.`      → `Doe, John`
 *   `Smith`               → `Smith`
 *   `^FirstOnly`          → `FirstOnly`
 *   `田中^太郎=Tanaka^Taro` → `Tanaka, Taro` (alphabetic group only)
 */
export function formatDicomName(raw: string | null | undefined): string | null {
    if (raw == null) return null;
    const trimmed = raw.trim();
    if (trimmed === '') return null;

    // Alphabetic representation only (group 0 in DICOM PN spec).
    const alphabetic = trimmed.split('=')[0];
    const [family, given] = alphabetic.split('^');
    const familyName = toTitleCase(family);
    const givenName = toTitleCase(given);

    if (familyName && givenName) return `${familyName}, ${givenName}`;
    if (familyName) return familyName;
    if (givenName) return givenName;
    return trimmed;
}

/**
 * Convert a DICOM DA (Date) string (`YYYYMMDD`) to the browser locale
 * date format. Returns the raw value unchanged if it doesn't match the
 * 8-digit pattern (so partial / vendor-quirk values are still
 * readable).
 */
export function formatDicomDate(yyyymmdd: string | null | undefined): string | null {
    if (yyyymmdd == null) return null;
    if (!/^\d{8}$/.test(yyyymmdd)) return yyyymmdd || null;

    const year = Number(yyyymmdd.slice(0, 4));
    const month = Number(yyyymmdd.slice(4, 6)) - 1;
    const day = Number(yyyymmdd.slice(6, 8));
    const date = new Date(Date.UTC(year, month, day));
    if (Number.isNaN(date.getTime())) return yyyymmdd;

    return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        timeZone: 'UTC',
    });
}

function toTitleCase(part: string | undefined): string {
    if (!part) return '';
    // DICOM names are commonly stored UPPER-CASED; soften to title
    // case for display. Preserve internal hyphens / apostrophes.
    return part
        .toLowerCase()
        .replace(/(^|[\s\-'])(\S)/g, (_, sep: string, ch: string) => sep + ch.toUpperCase())
        .trim();
}
